import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  type Contact,
  type CreateContactDto,
  type Env,
  type Partner,
  PartnerApi,
  CreditSafeApi,
  type UpdateEnvDto,
  type UpdatePartnerDto,
  type PartnerProjection,
  type PartnerAddressProjection,
  type ParentAttributionDto,
  PartnerAttributionApi,
} from '@/api-swagger'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

function mapOdooPartnerToSwagger(raw: any): Partner {
  const parentId = raw.parent_id
  return {
    id: raw.id,
    name: raw.name,
    siret: raw.siret ?? null,
    image1920: raw.image_1920 ?? null,
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    mobile: raw.mobile ?? null,
    street: raw.street ?? null,
    city: raw.city ?? null,
    zip: raw.zip ?? null,
    contactAddress: raw.contact_address ?? null,
    totalInvoiced: raw.total_invoiced ?? null,
    creditSafeValue: raw.credit_safe_value ?? null,
    creditSafeRiskText: raw.credit_safe_risk_text ?? null,
    saleWarn: raw.sale_warn ?? null,
    saleWarnMsg: raw.sale_warn_msg ?? null,
    parentId: parentId && Array.isArray(parentId)
      ? { id: parentId[0], name: parentId[1], siret: null }
      : parentId && typeof parentId === 'object'
      ? { id: parentId.id, name: parentId.name, siret: parentId.siret ?? null }
      : null,
    partnerLatitude: raw.partner_latitude ?? null,
    partnerLongitude: raw.partner_longitude ?? null,
    userId: raw.user_id && Array.isArray(raw.user_id)
      ? { id: raw.user_id[0], name: raw.user_id[1], email: null }
      : null,
    active: raw.active ?? true,
    type: raw.type ?? null,
    comment: raw.comment ?? null,
    currentStatus: raw.current_status ?? null,
    creditSafe: raw.credit_safe ?? false,
    nafCode: raw.naf_code ?? null,
    companyWorkforce: raw.company_workforce ?? null,
    xCreditStatus: raw.x_credit_status ?? null,
    xCa: raw.x_ca ?? null,
    odooUrl: raw.odooUrl ?? null,
  } as unknown as Partner
}

export const usePartnerStore = defineStore('partner', () => {
  const searchValue = ref('')
  const searchPartners = ref<Partner[]>([])
  const resultValue = ref<Partner[] | null>(null)
  const resultSiretValue = ref<Partner[] | null>(null)
  const childs = ref<Contact[] | null>(null)
  const userContact = ref<Contact[] | null>(null)
  const partner = ref<Partner | null>(null)
  const partnerForEventSelected = ref<PartnerProjection | null>(null)
  const partnerAddressForEventSelected = ref<PartnerAddressProjection | null>(null)
  const partnerForCreditSafe = ref<Partner | null>(null)
  const companies = ref<Partner[] | null>(null)
  const hasSearched = ref(false)
  const isActive = ref(false)
  const loading = ref(false)
  const creatingPartner = ref(false)
  const searchContacts = ref<Contact[] | null>(null)
  const loadingContacts = ref(false)
  const childsCompanyUuid = ref<string | null>(null)
  const loadingPartners = ref(false)
  const error = ref<string | null>(null)
  const env = ref<Env | null>(null)
  const api = new PartnerApi()
  const apiCreditSafe = new CreditSafeApi()
  const apiAttribution = new PartnerAttributionApi()

  const searchSiret = async (siret: string): Promise<void> => {
    hasSearched.value = true
    isActive.value = true
    searchPartners.value = []

    if (siret.length !== 9 && siret.length !== 14) {
      searchPartners.value = []
      loadingPartners.value = false
      return
    }

    loadingPartners.value = true

    try {
      const response = await api.getBySiretInclude({ siret })

      if (response?.length) {
        searchPartners.value = response
        return
      }

      if (siret.length === 14) {
        siret = siret.substring(0, 9)
      }
      try {
        const creditSafeResponse = await apiCreditSafe.getCompaniesBySiren({ siren: siret })

        if (creditSafeResponse?.length) {
          searchPartners.value = creditSafeResponse
        }
        isActive.value = true
      } catch (error) {
        console.error('Erreur lors de l’appel à CreditSafe API :', error)
      }
    } catch (error) {
      console.error('Erreur lors de la recherche par SIRET:', error)
    } finally {
      loadingPartners.value = false
      hasSearched.value = true
    }
  }

  const searchSiretOnly = async (siret: string): Promise<void> => {
    if (siret.length !== 14) {
      return
    }

    loading.value = true
    resultSiretValue.value = []

    try {
      const result = await api.getBySiretInclude({ siret })
      resultSiretValue.value = result || []
    } catch (error) {
      console.error('Erreur lors de la recherche par SIRET:', error)
    } finally {
      loading.value = false
    }
  }

  const searchId = async (id: number) => {
    loading.value = true
    partner.value = null
    try {
      const suiteStore = useSuiteStore()
      if (suiteStore.isSuiteAvailable !== false) {
        try {
          const res = await suiteStore.suiteFetch(`partner/${id}`, {
            method: 'GET',
            credentials: 'include',
          })
          if (res && res.ok) {
            const raw = await res.json()
            if (raw && raw.id) {
              partner.value = mapOdooPartnerToSwagger(raw)
              return partner.value
            }
          }
        } catch {
          // fallback to BS2 api
        }
      }
      partner.value = (await api.getOneById({ id: id })) || null
      return partner.value
    } catch (err: any) {
      error.value = err.message || "Une erreur s'est produite lors de l'édition du contact'."
    } finally {
      loading.value = false
    }
  }

  const searchCreditSafeValuesByPartnerId = async (id: number) => {
    loading.value = true
    try {
      partnerForCreditSafe.value = (await api.getOneById({ id: id })) || null
      return partnerForCreditSafe.value
    } catch (err: any) {
      error.value = err.message || "Une erreur s'est produite lors de la recherche de la société'."
    } finally {
      loading.value = false
    }
  }

  const searchPartnerByText = async (text: string) => {
    loading.value = true
    loadingPartners.value = true
    hasSearched.value = true
    isActive.value = true
    searchPartners.value = []
    try {
      searchPartners.value = (await api.searchByNameInclude({ name: text })) || null
      return partner.value
    } catch (err: any) {
      error.value = err.message || "Une erreur s'est produite lors la recherche de la société."
    } finally {
      loadingPartners.value = false
      loading.value = false
    }
  }

  const createContact = async (createContactDto: CreateContactDto) => {
    return withApiState(
      async () => {
        const response = await api.createContact({ createContactDto })
        await getContactsByParentId(createContactDto.parentId)
        return response
      },
      { loading, logLabel: 'Error creating contact:', rethrow: true },
    )
  }

  const createBySiret = async (siret: string) => {
    await withApiState(
      async () => {
        partner.value = (await api.createBySiret({ siret: siret }))[0] || null
      },
      { loading: creatingPartner, rethrow: true },
    )
  }

  const getPartnerById = async (id: number) => {
    return withApiState(
      async () => {
        const response = await api.getOneById({ id })
        if (response) {
          return response
        } else {
          throw new Error('Partner non trouvée.')
        }
      },
      { loading, error, logLabel: 'Erreur API :' },
    )
  }

  const getPartnersByListId = async (ids: string[]) => {
    return withApiState(
      async () => {
        const response = await api.findPartnersByListId({ requestBody: ids })
        if (response) {
          return response
        } else {
          throw new Error('Partner non trouvée.')
        }
      },
      { loading, error, logLabel: 'Erreur API :' },
    )
  }

  const getPartnerNameById = async (id: number) => {
    return withApiState(
      async () => {
        if (!id) {
          partnerForEventSelected.value = null
          return
        }
        const response = await api.getPartnerNameById({ id })
        if (response) {
          partnerForEventSelected.value = response
          return partnerForEventSelected.value.name
        } else {
          throw new Error('Partner non trouvée.')
        }
      },
      { loading, error, logLabel: 'Erreur API :' },
    )
  }

  const getPartnerAddressById = async (id: number) => {
    await withApiState(
      async () => {
        if (!id) {
          partnerAddressForEventSelected.value = null
          return
        }
        const response = await api.getPartnerAddressById({ id })
        if (response) {
          partnerAddressForEventSelected.value = response
        } else {
          throw new Error('Partner non trouvée.')
        }
      },
      { loading, error, logLabel: 'Erreur API :' },
    )
  }

  const getProspectEnvById = async (id: number) => {
    loading.value = true
    error.value = null
    env.value = null

    try {
      const suiteStore = useSuiteStore()
      if (suiteStore.isSuiteAvailable !== false) {
        try {
          const res = await suiteStore.suiteFetch(`partner/env/${id}`, { method: 'GET', credentials: 'include' })
          if (res && res.ok) {
            const raw = await res.json()
            if (raw && raw.id) {
              env.value = {
                operatorMobileDate: raw.operator_mobile_date || null,
                operatorMobileNbline: raw.operator_mobile_nbline || null,
                operatorMobileCompetitorName: raw.operator_mobile_competitor_name || null,
                operatorFixeDate: raw.operator_fixe_date || null,
                operatorFixeNbline: raw.operator_fixe_nbline || null,
                operatorFixeCompetitorName: raw.operator_fixe_competitor_name || null,
                operatorDataDate: raw.operator_data_date || null,
                operatorDataTechnology: raw.operator_data_Technology || null,
                operatorDataCompetitorName: raw.operator_data_competitor_name || null,
                integratorDate: raw.integrator_date || null,
                integratorNbchannel: raw.integrator_nbchannel || null,
                integratorCompetitorName: raw.integrator_competitor_name || null,
              } as any
              return
            }
          }
        } catch {
          // fallback
        }
      }
      const response = await api.getProspectEnvById({ id })
      if (response) {
        env.value = response
      } else {
        throw new Error('Partner non trouvée.')
      }
    } catch (err) {
      console.error('Erreur API :', err)
    } finally {
      loading.value = false
    }
  }

  const searchByLocation = async (lat: number, lng: number, radius: number) => {
    return withApiState(
      async () => {
        const response = await api.getPartnersByLocation({ lat, lng, radius })
        if (response) return response
      },
      { loading, logLabel: 'Erreur API :' },
    )
  }

  const updatePartner = async (id: number, data: UpdatePartnerDto) => {
    loading.value = true
    error.value = null

    try {
      const suiteStore = useSuiteStore()
      const suiteResponse = await suiteStore.suiteFetch(`partner/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (suiteResponse && suiteResponse.ok) {
        const updatedPartner = await getPartnerById(id)
        if (partner.value && partner.value.id === id) {
          partner.value = { ...partner.value, ...updatedPartner }
        }
        return updatedPartner
      }
      const response = await api.updatePartnerById({ id: id, updatePartnerDto: data })
      if (response) {
        const updatedPartner = await getPartnerById(id)
        if (partner.value && partner.value.id === id) {
          partner.value = { ...partner.value, ...updatedPartner }
        }
        return updatedPartner
      }
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour du contact.' + err)
    } finally {
      loading.value = false
    }
  }

  const updateEnvByPartnerId = async (id: number, data: UpdateEnvDto) => {
    loading.value = true
    error.value = null

    try {
      const suiteStore = useSuiteStore()
      if (suiteStore.isSuiteAvailable !== false) {
        const res = await suiteStore.suiteFetch(`partner/${id}/env`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })
        if (res && res.ok) {
          return
        }
      }
      const response = await api.updateEnvByPartnerId({ id: id, updateEnvDto: data })
      if (response) {
        const updatedPartner = await getPartnerById(id)
        if (partner.value && partner.value.id === id) {
          partner.value = { ...partner.value, ...updatedPartner }
        }
        return updatedPartner
      }
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour du contact.' + err)
    } finally {
      loading.value = false
    }
  }

  const updateAutoByPartnerId = async (id: number) => {
    loading.value = true
    error.value = null

    try {
      await api.autoUpdatePartnerById({ id: id })
      const updatedPartner = await getPartnerById(id)
      if (partner.value && partner.value.id === id) {
        partner.value = { ...partner.value, ...updatedPartner }
      }
      return updatedPartner
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour automatique.' + err)
    } finally {
      loading.value = false
    }
  }

  const updateScoreByPartnerId = async (id: number) => {
    loading.value = true
    error.value = null

    try {
      await api.updateScorePartnerById({ id: id })
      const partnerForCreditSafe = await getPartnerById(id)
      if (partner.value && partner.value.id === id) {
        partner.value = { ...partner.value, ...partnerForCreditSafe }
      }
      return partnerForCreditSafe
    } catch (err) {
      throw new Error('Erreur lors de la mise à jour automatique.' + err)
    } finally {
      loading.value = false
    }
  }

  const fetchCompanyByOdooRef = async (partnerId: number) => {
    childsCompanyUuid.value = null
    const suiteStore = useSuiteStore()
    if (suiteStore.isSuiteAvailable === false) return null
    try {
      const res = await suiteStore.suiteFetch(`companies/odoo/${partnerId}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && res.ok) {
        const data = await res.json()
        if (data?.uuid) {
          childsCompanyUuid.value = data.uuid
          return data
        }
      }
    } catch {
      // ignore
    }
    return null
  }

  // Pilotage par UUID de company (boost-suite) : renvoie la company
  // (dont `referenceExterne` = id Odoo du partner) et fixe `childsCompanyUuid`.
  const fetchCompanyByUuid = async (companyUuid: string) => {
    childsCompanyUuid.value = null
    const suiteStore = useSuiteStore()
    if (suiteStore.isSuiteAvailable === false) return null
    try {
      const res = await suiteStore.suiteFetch(`companies/${companyUuid}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && res.ok) {
        const data = await res.json()
        if (data?.uuid) {
          childsCompanyUuid.value = data.uuid
          return data
        }
      }
    } catch {
      // ignore
    }
    return null
  }

  const getContactsByParentId = async (id: number, user = false) => {
    loadingContacts.value = true
    error.value = null

    const api = new PartnerApi()

    if (user) {
      userContact.value = []
      userContact.value = (await api.getContactsByParentId({ id })) || []
      loadingContacts.value = false
      return userContact.value
    }

    childs.value = null

    const suiteStore = useSuiteStore()
    if (suiteStore.isSuiteAvailable !== false) {
      try {
        const res = await suiteStore.suiteFetch(`contacts/companyOdoo/${id}`, {
          method: 'GET',
          credentials: 'include',
        })
        if (res && res.ok) {
          const data = await res.json()
          const contacts = Array.isArray(data) ? data : (data?.contacts ?? [])
          const mapped = contacts.map((c: any) => ({
            ...c,
            name: [c.firstname, c.lastname].filter(Boolean).join(' ') || '',
            title: c.civility ? { name: c.civility } : null,
            _function: c.function || '',
            parentId: { id },
          }))
          childs.value = mapped
        }
      } catch {
        // fallback to BS2 api
      }
    }

    if (!childs.value) {
      const response = await api.getContactsByParentId({ id })
      if (response) {
        childs.value = response
      } else {
        childs.value = []
      }
    }
    loadingContacts.value = false
  }

  const getPartnersByParentId = async (id: number, user = false) => {
    loadingPartners.value = true
    error.value = null

    const api = new PartnerApi()

    if (user) {
      companies.value = (await api.getPartnersByParentId({ id })) || []
      loadingPartners.value = false
      return companies.value
    }

    const suiteStore = useSuiteStore()
    if (suiteStore.isSuiteAvailable !== false) {
      try {
        const res = await suiteStore.suiteFetch(`partner/${id}/company`, {
          method: 'GET',
          credentials: 'include',
        })
        if (res && res.ok) {
          const data = await res.json()
          companies.value = Array.isArray(data) ? data.map(mapOdooPartnerToSwagger) : []
          loadingPartners.value = false
          return companies.value
        }
      } catch {
        // fallback
      }
    }

    await api
      .getPartnersByParentId({ id })
      .then((response) => {
        if (response) {
          companies.value = response
        } else {
          throw new Error('Filiales non trouvées.')
        }
      })
      .catch((err) => {
        console.error('Erreur API :', err)
      })
      .finally(() => {
        loadingPartners.value = false
      })
  }

  const searchContactsByPartnerIds = async (partnerIds: string[]): Promise<void> => {
    await withApiState(
      async () => {
        if (!partnerIds || partnerIds.length === 0) {
          searchContacts.value = []
          return
        }
        const response = await api.getContactsByListParentId({ ids: partnerIds })
        if (!response) {
          throw new Error('Contacts non trouvés.')
        }
        searchContacts.value = response
      },
      { loading: loadingContacts, logLabel: 'Erreur API :' },
    )
  }

  const attributionParent = async (id: number, data: ParentAttributionDto) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.parentAttributionByChildId({ id: id, parentAttributionDto: data })
      if (response) {
        const updatedPartner = await getPartnerById(id)

        if (partner.value && partner.value.id === id) {
          // si on ajoute une société mère
          partner.value = { ...partner.value, ...updatedPartner }
        } else {
          // sinon si on on ajoute une société enfant
          await getPartnersByParentId(data.parentId || partner.value!.id, true)
        }
        return updatedPartner
      }
    } catch (err) {
      error.value = 'Erreur lors de la mise à jour du client.' + err
      throw new Error('La société ne peut pas être liée')
    } finally {
      loading.value = false
    }
  }

  async function getCurrentStatus(partner: Partner) {
    const contacts = await api.getContactsByParentId({ id: partner.id })
    const attributions = await apiAttribution.getPartnerAttributionsByPartnerId({
      partnerId: partner.id,
    })
    if (attributions.length > 0) return 'client'
    if (contacts.length > 0) return 'prospect'

    return 'suspect'
  }

  return {
    searchValue,
    resultValue,
    resultSiretValue,
    childs,
    childsCompanyUuid,
    userContact,
    partner,
    partnerForEventSelected,
    partnerAddressForEventSelected,
    companies,
    env,
    hasSearched,
    isActive: isActive,
    loading,
    creatingPartner,
    loadingContacts,
    searchPartners,
    loadingPartners,
    searchContacts,
    partnerForCreditSafe,
    searchByLocation,
    searchSiret,
    searchSiretOnly,
    searchId,
    createBySiret,
    updatePartner,
    fetchCompanyByOdooRef,
    fetchCompanyByUuid,
    getContactsByParentId,
    getPartnersByListId,
    getPartnersByParentId,
    updateAutoByPartnerId,
    getPartnerNameById,
    getPartnerAddressById,
    getPartnerById,
    getProspectEnvById,
    updateScoreByPartnerId,
    searchCreditSafeValuesByPartnerId,
    searchPartnerByText,
    createContact,
    updateEnvByPartnerId,
    searchContactsByPartnerIds,
    attributionParent,
    getCurrentStatus,
  }
})
